if (!customElements.get('c-mega-builder')) {
  class CMegaBuilder extends HTMLElement {
    connectedCallback() {
      this.listeners = [];
      this.item = null;
      this.trigger = null;
      this.observer = null;
      this.passThrough = false;
      this.suppressFocusOpen = false;
      this.isMobile = this.classList.contains('c-mb--mobile');

      this.on(this, 'click', (event) => this.onClick(event));
      this.on(this, 'keydown', (event) => this.onKeydown(event));
      this.on(this, 'focusout', (event) => this.onFocusOut(event));

      if (!this.isMobile) {
        this.querySelectorAll('.c-mbl__item--parent').forEach((li) => {
          if (this.isFloating(li)) this.on(li, 'mouseleave', () => this.setOpen(li, false));
        });
        this.bindDropdown();
      }
    }

    disconnectedCallback() {
      this.listeners.forEach(([el, type, fn, capture]) => el.removeEventListener(type, fn, capture));
      this.listeners = [];
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.item && this.item.dataset.cMbBound === (this.dataset.blockId || '1')) {
        delete this.item.dataset.cMbBound;
      }
      this.item = null;
      this.trigger = null;
    }

    on(el, type, fn, capture) {
      el.addEventListener(type, fn, !!capture);
      this.listeners.push([el, type, fn, !!capture]);
    }

    /* ---------- Sub-menus: accordion / dropdown / flyout ---------- */
    panelOf(li) {
      return li ? li.querySelector(':scope > .c-mbl__panel') : null;
    }

    toggleOf(li) {
      return li ? li.querySelector(':scope > .c-mbl__row > .c-mbl__toggle') : null;
    }

    isFloating(li) {
      const panel = this.panelOf(li);
      return !!panel && (panel.classList.contains('c-mbl__panel--dropdown') || panel.classList.contains('c-mbl__panel--flyout'));
    }

    setOpen(li, open) {
      if (!li) return;
      const btn = this.toggleOf(li);
      if (open && this.isFloating(li) && li.parentElement) {
        Array.prototype.forEach.call(li.parentElement.children, (sib) => {
          if (sib !== li && sib.classList.contains('is-open')) this.setOpen(sib, false);
        });
      }
      if (!open) {
        li.querySelectorAll('.c-mbl__item.is-open').forEach((child) => this.setOpen(child, false));
      }
      li.classList.toggle('is-open', open);
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onClick(event) {
      const toggle = event.target.closest('.c-mbl__toggle');
      if (toggle && this.contains(toggle)) {
        event.preventDefault();
        const li = toggle.closest('.c-mbl__item');
        this.setOpen(li, !li.classList.contains('is-open'));
        return;
      }
      const add = event.target.closest('[data-c-mb-add]');
      if (add && this.contains(add)) {
        event.preventDefault();
        this.addToCart(add);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = event.target.closest('.c-mbl__item.is-open');
      if (!open || !this.contains(open)) return;
      event.stopPropagation();
      this.setOpen(open, false);
      const btn = this.toggleOf(open);
      if (btn) btn.focus();
    }

    onFocusOut(event) {
      if (this.isMobile) return;
      const next = event.relatedTarget;
      this.querySelectorAll('.c-mbl__item.is-open').forEach((li) => {
        if (!this.isFloating(li)) return;
        if (next && li.contains(next)) return;
        this.setOpen(li, false);
      });
    }

    /* ---------- Add to cart (Product item) ---------- */
    async addToCart(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const variantId = Number(btn.dataset.cMbAdd);
      if (!variantId) return;
      const msg = btn.parentElement ? btn.parentElement.querySelector('.c-mbw-prod__msg') : null;
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('is-error');
      }
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          if (msg) {
            msg.textContent = data.description || data.message || 'Could not add to cart.';
            msg.classList.add('is-error');
          }
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      } catch (err) {
        if (msg) {
          msg.textContent = 'Could not add to cart. Please try again.';
          msg.classList.add('is-error');
        }
      } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    }

    /* ---------- Top-level dropdown: hover / click, keyboard, editor ----------
       The theme's SiteNav opens a dropdown on mouseenter of .m-menu__item and closes it
       on mouseleave. We replay those events so delay, animation and overlay stay native. */
    isOpen() {
      return !!this.item && this.item.classList.contains('m-menu__item--active');
    }

    open() {
      if (!this.item || this.isOpen()) return;
      this.passThrough = true;
      this.item.dispatchEvent(new MouseEvent('mouseenter'));
      this.passThrough = false;
    }

    close() {
      if (!this.item || !this.isOpen()) return;
      this.querySelectorAll('.c-mbl__item.is-open').forEach((li) => {
        if (this.isFloating(li)) this.setOpen(li, false);
      });
      this.passThrough = true;
      this.item.dispatchEvent(new MouseEvent('mouseleave'));
      this.passThrough = false;
    }

    bindDropdown() {
      const item = this.closest('.m-menu__item');
      if (!item || item.dataset.cMbBound) return;
      item.dataset.cMbBound = this.dataset.blockId || '1';
      this.item = item;
      const clickMode = this.dataset.openOn === 'click';
      const trigger = item.querySelector(':scope > .m-menu__link--main');
      this.trigger = trigger;

      if (trigger) {
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        this.observer = new MutationObserver(() => {
          trigger.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
        });
        this.observer.observe(item, { attributes: true, attributeFilter: ['class'] });
      }

      if (clickMode) {
        const block = (event) => {
          if (!this.passThrough) event.stopImmediatePropagation();
        };
        this.on(item, 'mouseenter', block, true);
        this.on(item, 'mouseleave', block, true);
        if (trigger) {
          this.on(trigger, 'click', (event) => {
            event.preventDefault();
            if (this.isOpen()) this.close();
            else this.open();
          });
        }
        this.on(document, 'click', (event) => {
          if (this.isOpen() && !item.contains(event.target)) this.close();
        });
        if (item.parentElement) {
          Array.prototype.forEach.call(item.parentElement.children, (sib) => {
            if (sib !== item) this.on(sib, 'mouseenter', () => this.close());
          });
        }
      } else {
        this.on(item, 'focusin', () => {
          if (!this.suppressFocusOpen) this.open();
        });
      }

      this.on(item, 'focusout', (event) => {
        const next = event.relatedTarget;
        if (next && item.contains(next)) return;
        if (!clickMode && item.matches(':hover')) return;
        if (clickMode && !next) return;
        this.close();
      });

      this.on(item, 'keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen()) return;
        this.close();
        if (trigger) {
          this.suppressFocusOpen = true;
          trigger.focus();
          this.suppressFocusOpen = false;
        }
      });

      this.on(document, 'shopify:block:select', (event) => {
        if (event.target === this || this.contains(event.target)) this.open();
      });
      this.on(document, 'shopify:block:deselect', (event) => {
        if (event.target === this || this.contains(event.target)) this.close();
      });
    }
  }

  customElements.define('c-mega-builder', CMegaBuilder);
}
